import React, { useEffect, useState, useRef } from "react";
import AuthenticationHOC from "../AuthenticationHOC/AuthenticationHOC";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { StyledFormField, StyledFormLayout } from "../styled/styled-components";
import SearchIcon from "../../assets/search";
import TimesIcon from "../../assets/times";

export interface Address {
  FullAddress: string;
  placeId?: string;
  DPID?: number;
}

interface Props {
  setSearchValue: (address: Address | null) => void;
  generateAuthHeader: () => RequestInit;
  clearForm: () => void;
  label?: string;
  defaultValue?: string;
}

const AddressSearcher = ({
  setSearchValue,
  generateAuthHeader,
  clearForm,
  label = "Search address",
  defaultValue = "",
}: Props) => {
  const [searchInput, setSearchInput] = useState(defaultValue);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const timeoutRef = useRef<number>();

  useEffect(() => {
    setSearchInput(defaultValue);
  }, [defaultValue]);

  const fetchAddresses = async (input: string) => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const url = `${process.env.REACT_APP_HALFBACK_ROOT_API}/address_check?address_line_1=${input}`;
      const res = await fetch(url, generateAuthHeader());
      const data = await res.json();

      if (data.result?.length && data.status === "success") {
        setAddresses(data.result);
        setShowDropdown(true);
      } else {
        setAddresses([]);
        setErrorMessage("Could not find related addresses.");
      }
    } catch {
      setAddresses([]);
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchInput(value);
    clearForm();

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (!value) {
      setAddresses([]);
      setErrorMessage("");
      setShowDropdown(false);
      return;
    }

    timeoutRef.current = window.setTimeout(() => fetchAddresses(value), 700);
  };

  const handleSelectAddress = (address: Address) => {
    const formattedAddress =
      address.FullAddress.charAt(0).toUpperCase() + address.FullAddress.slice(1);
    setSearchInput(formattedAddress);
    setSearchValue(address);
    setShowDropdown(false);
  };

  const handleClear = () => {
    setSearchInput("");
    setAddresses([]);
    setErrorMessage("");
    setShowDropdown(false);
    clearForm();
  };

  return (
    <StyledFormLayout>
      <StyledFormField>
        <label>{label}</label>
        <div className="address-input-wrapper">
          {isLoading ? (
            <LoadingSpinner inline />
          ) : (
            <SearchIcon width={16} height={16} className="search-icon" />
          )}
          <input
            aria-label="address-search-field"
            value={searchInput}
            onChange={handleInputChange}
            placeholder="Start typing an address..."
          />
          {searchInput && (
            <button
              type="button"
              className="clear-button"
              onClick={handleClear}
              aria-label="Clear search"
            >
              <TimesIcon width={12} height={12} />
            </button>
          )}
        </div>
      </StyledFormField>

      {errorMessage && (
        <div className="address-error">
          <span>{errorMessage}</span>
        </div>
      )}

      {showDropdown && addresses.length > 0 && (
        <div className="address-searcher">
          {addresses.map((address, index) => (
            <div
              key={address.placeId || index}
              className="address-suggestion"
              role="option"
              onClick={() => handleSelectAddress(address)}
            >
              {address.FullAddress}
            </div>
          ))}
        </div>
      )}
    </StyledFormLayout>
  );
};

export default AuthenticationHOC(AddressSearcher);
